import { UserOutlined } from "@ant-design/icons";
import { Avatar, Button, Card, Space, Tabs, Typography } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Candidate from "redux/models/candidate";
import {
  selectIsClientHasEngagementMilestones,
  selectUser,
} from "redux/slices/userSlice";
import K from "utilities/constants";
import { isPermissionPresent } from "utilities/generalUtility";
import classNames from "classnames";
import CandidateComments from "./candidateComments";
import { CandidateJobEntryActivityTab } from "./candidateJobEntryActivityTab";
import CandidateIssueCredits from "./candidateIssueCredits";
import ScheduledBilling from "./scheduledBilling";
import CandidateAttachments from "./candidateAttachment";
import EngagementView from "./engagementView/engagementView";
import { CandidateDetailsContainer } from "./components/details/candidateDetailsContainer";
import { useIsAssignedToJob } from "./hooks/useIsAssignedToJob";

const { Title, Paragraph } = Typography;

export default function ViewActivity({
  styles,
  className,
  jobDetails,
  clientId,
  candidateActivity,
  setCandidateActivity,
  onCancel,
  renderHeaderActions = null,
  onStatusChanged = null,
  showEngagementTablePreview = false,
  defaultActiveTab = "details",
  onTabChange = null,
  hideContactDetails = false,
  onCandidateChanged = null,
  refreshCandidateDisplay = null,
}) {
  const userSlice = useSelector(selectUser);
  const isClientHasEngagementMilestones = useSelector((state) =>
    selectIsClientHasEngagementMilestones(
      state,
      candidateActivity.jobSource.clientsId,
    ),
  );

  const hasViewAttachmentPermission = isPermissionPresent(
    K.Permissions.ViewAttachment,
    userSlice.roles,
  );
  const isAssignedToJob = useIsAssignedToJob(jobDetails);
  const isAttachmentsTabVisible =
    hasViewAttachmentPermission && isAssignedToJob;

  const isCommentsTabVisible = isPermissionPresent(
    K.Permissions.ViewCandidateComments,
    userSlice.roles,
  );

  const isBillingsTabVisible = isPermissionPresent(
    K.Permissions.ViewCandidateBillings,
    userSlice.roles,
  );

  const isScheduledBillingsTabVisible = isPermissionPresent(
    K.Permissions.ViewCandidateScheduledBillings,
    userSlice.roles,
  );

  const [activeTabKey, setActiveTabKey] = useState(defaultActiveTab);
  const [statusHistory, setStatusHistory] = useState({});
  const [tabCounts, setTabCounts] = useState({
    commentsCount: null,
    attachmentsCount: null,
  });

  const fetchTabCounts = useCallback(async () => {
    try {
      const { data } = await Candidate.v2.getTabCounts(candidateActivity.id);
      setTabCounts(data);
    } catch (e) {
      console.error(e);
    }
  }, [candidateActivity.id]);

  useEffect(() => {
    fetchTabCounts();
  }, [fetchTabCounts]);

  useEffect(() => {
    if (activeTabKey === "attachments" && !isAttachmentsTabVisible) {
      setActiveTabKey("details");
      onTabChange?.("details");
    }
  }, [activeTabKey, isAttachmentsTabVisible, onTabChange]);

  const handleCommentsCountChange = useCallback((count) => {
    setTabCounts((prev) => ({
      commentsCount: count,
      attachmentsCount: prev.attachmentsCount,
    }));
  }, []);

  const handleAttachmentsCountChange = useCallback((count) => {
    setTabCounts((prev) => ({
      commentsCount: prev.commentsCount,
      attachmentsCount: count,
    }));
  }, []);

  const isEngagementTabVisible = () => {
    if (isPermissionPresent(K.Permissions.SuperAdmin, userSlice.roles)) {
      return true;
    }
    const hasManageEngagementsPermission = isPermissionPresent(
      [
        K.Permissions.ManageEngagementsForAssignedCandidates,
        K.Permissions.ManageEngagementsForAssignedClients,
      ],
      userSlice.roles,
    );
    return hasManageEngagementsPermission && isClientHasEngagementMilestones;
  };

  return (
    <>
      {candidateActivity && (
        <Card className={classNames(styles.viewActivityModal, className)}>
          <div className={styles.viewActivityHeader}>
            <div className={styles.ClientProfile}>
              <Avatar
                size="large"
                icon={<UserOutlined className={styles.avatarIcon} />}
              />
              <div className={`${styles.clientName}edit-icon`}>
                <Title level={5} className="mb-0">
                  {[
                    candidateActivity.candidate.firstName,
                    candidateActivity.candidate.lastName,
                  ].join(" ")}
                </Title>
                <div className={styles.ClientProfileInfo}>
                  <Paragraph className={styles.seniorLogistician}>
                    {candidateActivity.candidate.currentTitle ??
                      K.NullPlaceholder}
                  </Paragraph>
                  <Space size={0}>
                    {renderHeaderActions?.({
                      statusHistory,
                      jobDetails,
                      styles,
                    })}
                  </Space>
                </div>
              </div>
            </div>
            <Button
              type="link"
              className={styles.editButton}
              onClick={onCancel}
              icon={<i className={`icon-closeable ${styles.editIcon}`} />}
            />
          </div>
          <Tabs
            activeKey={activeTabKey}
            className={`active border-clr ${styles.clientsTabs}`}
            onChange={(tab) => {
              setActiveTabKey(tab || "details");
              onTabChange?.(tab);
            }}
            items={[
              {
                key: "details",
                label: "Details",
                children: (
                  <CandidateDetailsContainer
                    clientId={clientId}
                    candidateActivity={candidateActivity}
                    setCandidateActivity={setCandidateActivity}
                    jobDetails={jobDetails}
                    onStatusChanged={onStatusChanged}
                    onCandidateChanged={onCandidateChanged}
                    onStatusHistoryChange={setStatusHistory}
                    hideContactDetails={hideContactDetails}
                    styles={styles}
                  />
                ),
              },
              ...(isCommentsTabVisible
                ? [
                    {
                      key: "comments",
                      label:
                        tabCounts.commentsCount !== null
                          ? `Comments (${tabCounts.commentsCount})`
                          : "Comments",
                      children: (
                        <CandidateComments
                          styles={styles}
                          userData={userSlice.details}
                          candidateActivity={candidateActivity}
                          onCountChange={handleCommentsCountChange}
                        />
                      ),
                    },
                  ]
                : []),
              ...(isBillingsTabVisible
                ? [
                    {
                      key: "billings",
                      label: "Billing",
                      children: (
                        <CandidateIssueCredits
                          styles={styles}
                          candidateActivity={candidateActivity}
                        />
                      ),
                    },
                  ]
                : []),
              {
                key: "activities",
                label: "Activity",
                children: (
                  <CandidateJobEntryActivityTab
                    styles={styles}
                    candidateJobEntryId={candidateActivity.id}
                    isActive={activeTabKey === "activities"}
                  />
                ),
              },
              ...(isScheduledBillingsTabVisible
                ? [
                    {
                      key: "scheduledBillings",
                      label: "Scheduled Billing",
                      children: (
                        <ScheduledBilling
                          candidateActivity={candidateActivity}
                          styles={styles}
                        />
                      ),
                    },
                  ]
                : []),
              ...(isEngagementTabVisible()
                ? [
                    {
                      key: "engagements",
                      label: "Engagements",
                      children: (
                        <EngagementView
                          isActive={activeTabKey === "engagements"}
                          candidateJobEntry={candidateActivity}
                          isPreview={showEngagementTablePreview}
                        />
                      ),
                    },
                  ]
                : []),
              ...(isAttachmentsTabVisible
                ? [
                    {
                      key: "attachments",
                      label:
                        tabCounts.attachmentsCount !== null
                          ? `Attachments (${tabCounts.attachmentsCount})`
                          : "Attachments",
                      children: (
                        <CandidateAttachments
                          candidateActivity={candidateActivity}
                          styles={styles}
                          onCountChange={handleAttachmentsCountChange}
                        />
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </Card>
      )}
    </>
  );
}
